import { assertUnreachable } from '@shared/helpers/function.helpers';
import type { CadGeometryExtractResult } from './cad-outline-text.helpers';
import { extractDxfGeometryText } from './dxf-geometry-text.helpers';
import { extractIfcGeometryText } from './ifc-geometry-text.helpers';

/**
 * Extracts the geometry-bearing part of text-based CAD exchange files so it can
 * be handed to an LLM as source text.
 *
 * The AI providers' file APIs reject CAD MIME types, so uploading a `.dxf` or
 * `.ifc` as a file always fails. Both formats are reduced to the same compact
 * world-space outline records (meters, origin-shifted, +x east +y south).
 * DXF INSERT/BLOCKS and IFC local placements are exploded — see
 * {@link extractDxfGeometryText} and {@link extractIfcGeometryText}.
 */

export const CAD_TEXT_EXTENSIONS = Object.freeze(['.dxf', '.ifc'] as const);

export type CadTextExtension = (typeof CAD_TEXT_EXTENSIONS)[number];

/**
 * Byte encoding for CAD uploads. DXF `$DWGCODEPAGE` is typically ANSI_1252
 * (latin1); IFC STEP files are UTF-8.
 */
export const CAD_SOURCE_TEXT_ENCODING: Record<
  CadTextExtension,
  'latin1' | 'utf8'
> = {
  '.dxf': 'latin1',
  '.ifc': 'utf8',
};

const CAD_TEXT_EXTENSIONS_SET: ReadonlySet<string> = new Set(
  CAD_TEXT_EXTENSIONS,
);

/**
 * Whether a lowercase file extension (including the dot) is a CAD format we can
 * read as text.
 */
export const isCadTextExtension = (
  extension: string,
): extension is CadTextExtension => CAD_TEXT_EXTENSIONS_SET.has(extension);

/** Character budget handed to the model — large drawings are cut, not rejected. */
const CAD_TEXT_MAX_CHARS = 200_000;

export type CadGeometryTextResult = CadGeometryExtractResult;

type ExtractCadGeometryTextInput = {
  extension: CadTextExtension;
  /** Full file contents. DXF is typically ANSI/latin1; IFC is UTF-8. */
  source: string;
  maxChars?: number;
};

/**
 * Reduce a CAD export to the lines that describe geometry.
 *
 * @returns `ok` with the reduced text, or `unreadable` with a message that can
 * be shown to the user (e.g. binary DXF, or a drawing without any curves).
 */
export const extractCadGeometryText = (
  input: ExtractCadGeometryTextInput,
): CadGeometryTextResult => {
  const maxChars = input.maxChars ?? CAD_TEXT_MAX_CHARS;
  switch (input.extension) {
    case '.dxf':
      return extractDxfGeometryText(input.source, maxChars);
    case '.ifc':
      return extractIfcGeometryText(input.source, maxChars);
    default:
      return assertUnreachable(input.extension);
  }
};
